/**
 * IntegrationLoads.js — Onda 109
 * CI Sistemas de Informática Ltda. — AcessoiD
 *
 * As cargas da API de Integração e o relatório de não conformidades de cada
 * uma. Vale nas duas verticais que recebem cadastro por API: Clube Social
 * (associados) e Condomínio (unidades e moradores).
 *
 * O caso que originou a tela: em 22/09/2026 uma carga do Ipê recusou 590 de
 * 7.870 itens e ninguém viu — a resposta de lote parcial é 207, o integrador
 * seguiu em frente, e 295 associados ficaram fora do cadastro. Descobrir isso
 * exigiu abrir o banco de produção. Esta tela existe para que não precise.
 *
 * A lista mostra a data e a hora de cada carga; clicar abre o relatório.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import {
  FiRefreshCw, FiArrowLeft, FiDownload, FiSearch, FiX,
  FiAlertTriangle, FiCheckCircle, FiInfo, FiClock, FiKey, FiUploadCloud,
} from 'react-icons/fi';
import { explicacao, observacoesAtivas, veredito } from '../data/naoConformidades';
import './IntegrationLoads.css';

const dataHora = (v) => (v ? new Date(v).toLocaleString('pt-BR') : '—');
const soHora = (v) => (v ? new Date(v).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '—');
const numero = (n) => (Number(n) || 0).toLocaleString('pt-BR');

/** No clube o cadastro é o associado; no condomínio, a unidade. */
const nomeDoCadastro = (vertical, plural = false) => (vertical === 'clube_social'
  ? (plural ? 'associados' : 'associado')
  : (plural ? 'unidades' : 'unidade'));

export default function IntegrationLoads() {
  const { vertical } = useAuth();
  const [cargas, setCargas] = useState([]);
  const [total, setTotal] = useState(0);
  const [relatorio, setRelatorio] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [busca, setBusca] = useState('');

  const buscarCargas = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.get('/api/integration-loads?limit=50');
      setCargas(r.data.items || []);
      setTotal(r.data.total || 0);
      setErro(null);
    } catch (e) {
      setErro(e.response?.data?.error || 'Não foi possível carregar as cargas.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { buscarCargas(); }, [buscarCargas]);

  const abrirRelatorio = async (id) => {
    setCarregando(true);
    try {
      const r = await api.get(`/api/integration-loads/${id}`);
      setRelatorio(r.data);
      setBusca('');
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível abrir o relatório.');
    } finally {
      setCarregando(false);
    }
  };

  const exportarItens = () => {
    const itens = relatorio?.itens || [];
    const cabecalho = ['cadastro', 'placa', 'causa', 'mensagem', 'quando'];
    const linhas = itens.map(i => [
      i.entity_code || '', i.plate || '', explicacao(i.kind).titulo,
      String(i.message || '').replace(/;/g, ','), dataHora(i.detected_at),
    ].join(';'));
    const csv = `﻿${[cabecalho.join(';'), ...linhas].join('\n')}`;
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `nao_conformidades_carga_${relatorio.carga.id}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  // ─── Lista de cargas ───────────────────────────────────────────────────────
  if (!relatorio) {
    return (
      <div className="il-page">
        <div className="page-header">
          <div>
            <h1><FiUploadCloud /> Cargas da Integração</h1>
            <p>Cada carga de {nomeDoCadastro(vertical, true)} recebida pela API, com o que foi aceito e o que foi recusado</p>
          </div>
          <button className="btn btn-secondary" onClick={buscarCargas} disabled={carregando}>
            <FiRefreshCw className={carregando ? 'spin' : ''} /> Atualizar
          </button>
        </div>

        {erro && <div className="il-erro" role="alert">{erro}</div>}

        {!carregando && !cargas.length && !erro && (
          <div className="il-vazio">
            <FiUploadCloud size={44} />
            <h3>Nenhuma carga registrada ainda</h3>
            <p>
              Assim que o sistema de gestão enviar {nomeDoCadastro(vertical, true)} pela API de
              Integração, a carga aparece aqui — com data, hora e o relatório de não conformidades.
            </p>
          </div>
        )}

        {cargas.length > 0 && (
          <div className="il-lista">
            {cargas.map(c => {
              const grave = c.percentual_recusa >= 2;
              const algo = c.items_failed > 0;
              return (
                <button key={c.id} className={`il-carga ${grave ? 'grave' : algo ? 'atencao' : 'ok'}`}
                  onClick={() => abrirRelatorio(c.id)}>
                  <span className="il-carga-quando">
                    <strong>{dataHora(c.started_at)}</strong>
                    <span className="il-carga-duracao">
                      <FiClock /> {c.minutos > 0 ? `${c.minutos} min` : 'menos de 1 min'} · até {soHora(c.last_request_at)}
                    </span>
                  </span>
                  <span className="il-carga-numeros">
                    <span><b>{numero(c.items_total)}</b> itens</span>
                    <span className="ok"><b>{numero(c.items_ok)}</b> aceitos</span>
                    <span className={algo ? 'ruim' : ''}><b>{numero(c.items_failed)}</b> recusados</span>
                  </span>
                  <span className={`il-selo ${grave ? 'grave' : algo ? 'atencao' : 'ok'}`}>
                    {algo ? `${c.percentual_recusa}% recusado` : 'sem recusas'}
                  </span>
                  <span className="il-carga-chave">
                    <FiKey /> {c.api_key_name || 'chave removida'}
                    {c.declarada ? ' · execução declarada' : ''}
                  </span>
                </button>
              );
            })}
            {total > cargas.length && (
              <p className="il-nota-rodape">Mostrando as {cargas.length} cargas mais recentes de {total}.</p>
            )}
          </div>
        )}
      </div>
    );
  }

  // ─── Relatório de uma carga ────────────────────────────────────────────────
  const { carga, por_causa: porCausa, itens, ritmo } = relatorio;
  const observacoes = observacoesAtivas(relatorio.observacoes);
  const parecer = veredito(carga, observacoes);
  const itensFiltrados = busca
    ? itens.filter(i => `${i.entity_code || ''} ${i.plate || ''} ${i.message}`.toUpperCase().includes(busca.toUpperCase()))
    : itens;
  const picoRitmo = ritmo.reduce((m, l) => Math.max(m, l.requisicoes), 1);

  return (
    <div className="il-page">
      <div className="page-header">
        <div>
          <button className="btn btn-secondary btn-sm" onClick={() => setRelatorio(null)}>
            <FiArrowLeft /> Todas as cargas
          </button>
          <h1>Carga de {dataHora(carga.started_at)}</h1>
          <p>
            {carga.requests} requisiç{carga.requests === 1 ? 'ão' : 'ões'} ·
            {' '}{carga.minutos > 0 ? `${carga.minutos} minutos` : 'menos de um minuto'} ·
            {' '}chave {carga.api_key_name || 'removida'}
            {carga.external_sync_id ? ` · execução ${carga.external_sync_id}` : ''}
          </p>
        </div>
        {itens.length > 0 && (
          <button className="btn btn-secondary" onClick={exportarItens}>
            <FiDownload /> Exportar recusados
          </button>
        )}
      </div>

      <div className={`il-veredito ${parecer.tom}`}>
        {parecer.tom === 'bom' ? <FiCheckCircle /> : parecer.tom === 'ruim' ? <FiAlertTriangle /> : <FiInfo />}
        <span>{parecer.texto}</span>
      </div>

      <div className="il-placar">
        <div><span className="n">{numero(carga.items_total)}</span><span className="r">itens enviados</span></div>
        <div className="bom"><span className="n">{numero(carga.items_ok)}</span><span className="r">aceitos</span></div>
        <div className={carga.items_failed ? 'mau' : ''}>
          <span className="n">{numero(carga.items_failed)}</span><span className="r">recusados</span>
        </div>
        <div><span className="n">{numero(carga.created_entities)}</span><span className="r">{nomeDoCadastro(carga.vertical, true)} criados</span></div>
        <div><span className="n">{numero(carga.updated_entities)}</span><span className="r">atualizados</span></div>
        <div><span className="n">{numero(carga.created_vehicles + carga.updated_vehicles)}</span><span className="r">veículos gravados</span></div>
      </div>

      {porCausa.length > 0 && (
        <section className="il-secao">
          <h2>Não conformidades</h2>
          {porCausa.map(causa => {
            const texto = explicacao(causa.kind);
            return (
              <article key={causa.kind} className={`il-causa ${texto.gravidade}`}>
                <header>
                  <h3>{texto.titulo}</h3>
                  <span className="il-contagem">
                    {numero(causa.ocorrencias)} ocorrência{causa.ocorrencias === 1 ? '' : 's'}
                    {causa.cadastros > 0 && ` · ${numero(causa.cadastros)} ${nomeDoCadastro(carga.vertical, causa.cadastros !== 1)}`}
                    {causa.placas > 0 && ` · ${numero(causa.placas)} placa${causa.placas === 1 ? '' : 's'}`}
                  </span>
                </header>
                <p>{texto.oQueE}</p>
                <p className="il-efeito"><strong>Efeito:</strong> {texto.efeito}</p>
                <p className="il-correcao"><strong>Correção:</strong> {texto.correcao}</p>
              </article>
            );
          })}
        </section>
      )}

      {observacoes.length > 0 && (
        <section className="il-secao">
          <h2>Observações sobre a carga</h2>
          {observacoes.map(o => (
            <article key={o.chave} className={`il-causa ${o.gravidade}`}>
              <header>
                <h3>{o.titulo}</h3>
                {o.quantidade != null && <span className="il-contagem">{numero(o.quantidade)}×</span>}
              </header>
              <p>{o.texto}</p>
              <p className="il-correcao"><strong>Correção:</strong> {o.correcao}</p>
            </article>
          ))}
        </section>
      )}

      {ritmo.length > 1 && (
        <section className="il-secao">
          <h2>Ritmo da carga</h2>
          <p className="il-legenda">
            Requisições por minuto{carga.api_key_rate_limit ? `, contra o limite de ${carga.api_key_rate_limit} da chave` : ''}.
            {' '}Os lotes trouxeram {numero(carga.itens_por_requisicao)} itens em média, e o máximo aceito é 500.
          </p>
          <div className="il-ritmo">
            {ritmo.map(l => (
              <div key={l.minuto} className="il-barra" title={`${l.minuto} · ${l.requisicoes} requisições${l.bloqueadas ? `, ${l.bloqueadas} bloqueadas` : ''}`}>
                <span className={`il-barra-corpo ${l.bloqueadas ? 'bloqueada' : ''}`}
                  style={{ height: `${Math.max(6, (l.requisicoes / picoRitmo) * 100)}%` }} />
                <span className="il-barra-rotulo">{l.minuto}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {itens.length > 0 && (
        <section className="il-secao">
          <h2>Itens recusados</h2>
          <div className="il-busca">
            <FiSearch />
            <input placeholder="Filtrar por cadastro, placa ou mensagem"
              value={busca} onChange={e => setBusca(e.target.value)} />
            {busca && <button onClick={() => setBusca('')} aria-label="Limpar filtro"><FiX /></button>}
          </div>
          <div className="il-tabela-rolo">
            <table className="report-table">
              <thead>
                <tr>
                  <th>{vertical === 'clube_social' ? 'Associado' : 'Unidade'}</th>
                  <th>Placa</th>
                  <th>Causa</th>
                  <th>Mensagem devolvida ao integrador</th>
                </tr>
              </thead>
              <tbody>
                {itensFiltrados.length === 0 && (
                  <tr><td colSpan={4} className="il-vazio-linha">Nenhum item corresponde ao filtro.</td></tr>
                )}
                {itensFiltrados.map((i, idx) => (
                  <tr key={idx}>
                    <td className="il-mono">{i.entity_code || '—'}</td>
                    <td>{i.plate ? <span className="plate-mono">{i.plate}</span> : '—'}</td>
                    <td>{explicacao(i.kind).titulo}</td>
                    <td className="il-mensagem">{i.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="il-nota-rodape">
            Mostrando {itensFiltrados.length} de {itens.length} recusados. É o mesmo texto que o
            sistema de gestão recebeu na resposta da carga.
          </p>
        </section>
      )}
    </div>
  );
}
